import { useAppSelector } from '@frontend/common';
import { useModals } from '@frontend/modal';
import { addActiveFilter, listStore, removeActiveFilter } from '@frontend/repository';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { InsaneFilterProps } from './insane-filter.component';
import { getActiveFilterSyncFromSearchParams } from './sync-active-filters-from-search-params';

export const useInsaneFilter = (props: InsaneFilterProps) => {
    const store = useAppSelector(useSelector, listStore);
    const list = store[props.listId];
    const [searchParams, setSearchParams] = useSearchParams();
    const { openModal, closeModal } = useModals();
    const filterModalId = 'filter-modal';
    const options = useMemo(() => {
        const overwriteFilterKeys = props.filterKeys ? props.filterKeys.map((k) => props.queryKeyMap?.get(k) || k) : [];
        if (props.filterKeys && props.filterKeys.includes('waiting')) {
            return ['before', 'after', ...(props.searchKeys ?? []), ...overwriteFilterKeys];
        }
        return [...(props.searchKeys ?? []), ...overwriteFilterKeys];
    }, [props.searchKeys, props.filterKeys, props.queryKeyMap]);

    const listReady = list != null;
    const activeFilters = list?.activeFilters;
    // Read latest filters inside the effect without depending on them. Depending on
    // activeFilters re-runs sync with a stale URL after chip remove and re-adds the chip.
    const activeFiltersRef = useRef(activeFilters);
    activeFiltersRef.current = activeFilters;

    useEffect(() => {
        if (!listReady || options.length === 0) return;

        const { add, remove } = getActiveFilterSyncFromSearchParams(searchParams, options, activeFiltersRef.current);
        for (const { key, value } of add) {
            props.dispatch(addActiveFilter({ listId: props.listId, key, value }));
        }
        for (const key of remove) {
            props.dispatch(removeActiveFilter({ listId: props.listId, key }));
        }
    }, [props.listId, props.dispatch, listReady, searchParams, options]);

    return {
        list,
        searchParams,
        setSearchParams,
        openModal,
        closeModal,
        filterModalId
    };
};
